/**
 * Mailbox — read letters from Grandma.
 * Opens as an overlay on top of the paused GardenScene.
 *
 * Layout:
 *   top: "Mailbox" header + unread indicator
 *   scrollable list of letter cards (newest first)
 *   selected letter shows full handwritten text + Shimmer audio plays
 */
import Phaser from 'phaser';
import { LetterManager } from '../systems/LetterManager.js';

export class MailboxScene extends Phaser.Scene {
  constructor() { super({ key: 'MailboxScene' }); }

  init(data) {
    this._callerKey = data?.caller || 'GardenScene';
    this._autoOpenId = data?.openLetter || null;
  }

  create() {
    this._currentAudio = null;
    this._layout();
    this.scale.on('resize', this._onResize, this);
    // ESC / tap outside to close — done via a full-screen back button in layout
  }

  _onResize() {
    this.tweens.killAll();
    this.children.removeAll(true);
    this._layout();
  }

  shutdown() {
    this._stopAudio();
    this.scale.off('resize', this._onResize, this);
  }

  _layout() {
    const w = this.scale.width;
    const h = this.scale.height;

    // Dim backdrop (tap to close)
    const dim = this.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0.82).setDepth(0).setInteractive();
    dim.on('pointerdown', () => this._close());

    // Parchment panel
    const panelW = Math.min(w * 0.92, 440);
    const panelH = Math.min(h * 0.88, 760);
    const panelX = w / 2;
    const panelY = h / 2;

    const panel = this.add.graphics().setDepth(1);
    panel.fillStyle(0x3a2414, 0.96);
    panel.fillRoundedRect(panelX - panelW / 2, panelY - panelH / 2, panelW, panelH, 16);
    panel.lineStyle(2, 0xd4b08a, 0.8);
    panel.strokeRoundedRect(panelX - panelW / 2, panelY - panelH / 2, panelW, panelH, 16);
    // Inner parchment
    panel.fillStyle(0x4a3020, 0.98);
    panel.fillRoundedRect(panelX - panelW / 2 + 8, panelY - panelH / 2 + 8, panelW - 16, panelH - 16, 12);

    // Header
    this.add.text(panelX, panelY - panelH / 2 + 34, 'Letters from Grandma', {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '22px',
      fontStyle: '500',
      color: '#f4ecd8',
    }).setOrigin(0.5).setDepth(2);

    // Close X
    const closeBtn = this.add.text(panelX + panelW / 2 - 22, panelY - panelH / 2 + 22, '×', {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '30px',
      color: '#d4b08a',
    }).setOrigin(0.5).setDepth(3).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerdown', () => this._close());

    const letters = LetterManager.getLetters();

    if (letters.length === 0) {
      this.add.text(panelX, panelY, 'No letters yet.\n\nTend the garden together and\nshe will write to you.', {
        fontFamily: 'Cormorant Garamond, serif',
        fontSize: '16px',
        fontStyle: 'italic',
        color: '#a09080',
        align: 'center',
        lineSpacing: 6,
      }).setOrigin(0.5).setDepth(2);
      return;
    }

    // Either auto-open a specific letter or show the most recent
    const target = (this._autoOpenId && letters.find(l => l.id === this._autoOpenId)) || letters[0];
    this._showLetter(target, panelX, panelY, panelW, panelH);

    // If there are multiple letters, show navigation arrows
    if (letters.length > 1) {
      const idx = letters.findIndex(l => l.id === target.id);
      const hasPrev = idx < letters.length - 1; // letters[idx+1] is older
      const hasNext = idx > 0;                  // letters[idx-1] is newer

      const arrowY = panelY + panelH / 2 - 30;
      // Older
      const prevBtn = this.add.text(panelX - 60, arrowY, '‹ older', {
        fontFamily: 'Cormorant Garamond, serif',
        fontSize: '14px', fontStyle: 'italic',
        color: hasPrev ? '#d4b08a' : '#5a4a3a',
      }).setOrigin(0.5).setDepth(4);
      if (hasPrev) {
        prevBtn.setInteractive({ useHandCursor: true });
        prevBtn.on('pointerdown', () => { this._autoOpenId = letters[idx + 1].id; this._onResize(); });
      }
      // Newer
      const nextBtn = this.add.text(panelX + 60, arrowY, 'newer ›', {
        fontFamily: 'Cormorant Garamond, serif',
        fontSize: '14px', fontStyle: 'italic',
        color: hasNext ? '#d4b08a' : '#5a4a3a',
      }).setOrigin(0.5).setDepth(4);
      if (hasNext) {
        nextBtn.setInteractive({ useHandCursor: true });
        nextBtn.on('pointerdown', () => { this._autoOpenId = letters[idx - 1].id; this._onResize(); });
      }
      // Page indicator
      this.add.text(panelX, arrowY, `${idx + 1} / ${letters.length}`, {
        fontFamily: 'Cormorant Garamond, serif',
        fontSize: '12px', color: '#6a5a4a',
      }).setOrigin(0.5).setDepth(4);
    }
  }

  _showLetter(letter, panelX, panelY, panelW, panelH) {
    // Date stamp
    const stamp = new Date(letter.createdAt || Date.now()).toLocaleDateString(undefined, {
      weekday: 'long', month: 'long', day: 'numeric',
    });
    this.add.text(panelX, panelY - panelH / 2 + 68, stamp, {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '12px', fontStyle: 'italic',
      color: '#a09080',
    }).setOrigin(0.5).setDepth(2);

    // Letter body — handwritten feel via Cormorant italic
    const bodyY = panelY - panelH / 2 + 100;
    const bodyH = panelH - 180;
    const body = this.add.text(panelX, bodyY + bodyH / 2, letter.text || '...', {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '17px',
      fontStyle: 'italic',
      color: '#f4ecd8',
      align: 'left',
      wordWrap: { width: panelW - 60 },
      lineSpacing: 7,
    }).setOrigin(0.5).setDepth(2);

    // Mark as read
    LetterManager.markRead(letter.id);

    // Play Shimmer audio
    this._playLetterAudio(letter);
  }

  async _playLetterAudio(letter) {
    this._stopAudio();
    // If we have audioBase64 locally, use it; otherwise fetch the full letter
    let audioBase64 = letter.audioBase64;
    if (!audioBase64) {
      const full = await LetterManager.fetchFullLetter(letter.id);
      audioBase64 = full?.audioBase64;
    }
    if (!audioBase64) return;
    const dataUrl = `data:audio/mp3;base64,${audioBase64}`;
    try {
      const audio = new Audio(dataUrl);
      audio.volume = 0.9;
      audio.play().catch(() => {});
      this._currentAudio = audio;
    } catch (_) {}
  }

  _stopAudio() {
    if (this._currentAudio) {
      try { this._currentAudio.pause(); this._currentAudio.src = ''; } catch (_) {}
      this._currentAudio = null;
    }
  }

  _close() {
    this._stopAudio();
    this.scale.off('resize', this._onResize, this);
    this.scene.stop();
    const caller = this.scene.get(this._callerKey);
    if (caller && caller.scene.isPaused()) {
      this.scene.resume(this._callerKey);
    }
  }
}
